import type { QueryClient } from '@tanstack/react-query'

import { qk } from '@/api/query-keys'
import type { Application, Field, Table, View, ViewFieldOptions } from '@/api/types'
import { rowWindowsForTable } from '@/features/database/rows/rowWindowRegistry'
import { withFieldOptions } from '@/features/database/table/field-options'
import type { RealtimeClient, RealtimeContext } from '../client'
import type { RealtimeMessage } from '../protocol'
import {
  addFieldRule,
  removeFieldRule,
  updateFieldRule,
  type FieldRule,
} from './field-rules'
import { byTable, removeField, upsertField } from './fields'
import * as tables from './tables'
import * as views from './views'
import {
  prioritizeChildren,
  removeChild,
  upsertChild,
  type ChildList,
} from './view-children'

/**
 * The database realtime handlers.
 *
 * Tables, fields, views, the fifteen view-child events, field rules, and the
 * two panelled lists. The six that drive the row buffer live in `rows.ts`,
 * because the rows are not a cache entry.
 *
 * Ported from `web-frontend/modules/database/realtime.js`.
 *
 * Two lists are invalidated rather than patched, for the same reason as the
 * notification list: both are paginated reads whose shape belongs to whoever
 * builds the panel, and both live behind a panel that is usually closed, where
 * an invalidation costs a stale flag and no request.
 *
 * **Three events the backend sends are deliberately not registered here**, and
 * one upstream registers that this backend never sends.
 *
 * `view_decoration_created`, `view_decoration_updated` and
 * `view_decoration_deleted` have no consumer: nothing in this build reads a
 * decoration, and `viewsQuery` does not ask for them -- so a handler would
 * write a `decorations` key that the next refetch takes away again, leaving the
 * cached view disagreeing with itself. When decorations arrive they arrive as
 * an `include` and three handlers together.
 *
 * `rows_ai_values_generation_error` has no producer. The signal is declared and
 * received on the backend but nothing fires it any more: the only field type
 * that generated AI values left with the premium tree, and there is no `ai`
 * field type among the registry's twenty-seven.
 *
 * `force_view_refresh_and_default_values` is the one upstream listens for that
 * this backend does not send at all -- it appears nowhere outside the Vue
 * client.
 */

/**
 * Applies an update to every cached workspace's application list.
 *
 * None of the table events name a workspace, and `tables_reordered` does not
 * even give an unhashed database id, so there is nothing to narrow by. There is
 * one cached list per workspace the user has opened, so this is a handful of
 * arrays; the updaters return the array they were given when nothing matched,
 * and only a changed one is written back.
 */
function patchApplications(
  queryClient: QueryClient,
  update: (applications: Application[]) => Application[],
): void {
  const entries = queryClient.getQueriesData<Application[]>({
    queryKey: [...qk.all, 'applications'],
  })

  for (const [key, applications] of entries) {
    if (applications === undefined) continue
    const next = update(applications)
    if (next !== applications) queryClient.setQueryData<Application[]>(key, next)
  }
}

export function registerDatabaseHandlers(client: RealtimeClient): () => void {
  const off: (() => void)[] = []
  const on = (
    type: string,
    handler: (c: RealtimeContext, d: RealtimeMessage) => void,
  ) => {
    off.push(client.on(type, handler))
  }

  /** Every grid showing this table reads its rows again. */
  const refreshRows = (tableId: number): void => {
    for (const member of rowWindowsForTable(tableId)) member.store.getState().refresh()
  }

  /* --------------------------------------------------------------- tables */

  on('table_created', ({ queryClient }, data) => {
    const table = data.table as Table & { database_id: number }
    patchApplications(queryClient, (applications) =>
      tables.upsertTable(applications, table.database_id, table),
    )
  })

  on('table_updated', ({ queryClient }, data) => {
    const table = data.table as Table & { database_id: number }
    patchApplications(queryClient, (applications) =>
      tables.updateTable(applications, table.database_id, table.id, table),
    )
    // A data sync finished, or the table came back from the trash: the values
    // this tab is holding are not what the table contains any more.
    if (data.force_table_refresh === true) refreshRows(table.id)
  })

  on('table_deleted', ({ queryClient }, data) => {
    const databaseId = data.database_id as number
    const tableId = data.table_id as number
    patchApplications(queryClient, (applications) =>
      tables.removeTable(applications, databaseId, tableId),
    )
    queryClient.removeQueries({ queryKey: qk.table(tableId) })
  })

  on('tables_reordered', ({ queryClient }, data) => {
    patchApplications(queryClient, (applications) =>
      tables.orderTables(
        applications,
        data.database_id as string,
        data.order as string[],
      ),
    )
  })

  /* ---------------------------------------------------------------- views */

  on('view_created', ({ queryClient }, data) => {
    const view = data.view as View
    patchViews(queryClient, view.table_id, (held) => views.upsertView(held, view))
  })

  on('view_updated', ({ queryClient }, data) => {
    const view = data.view as View
    // Merged, never replaced. The payload leaves out the filters, sortings,
    // group-bys and decorations on purpose -- see `mergeView`.
    patchViews(queryClient, view.table_id, (held) => views.mergeView(held, view))
  })

  on('view_deleted', ({ queryClient }, data) => {
    const tableId = data.table_id as number
    const viewId = data.view_id as number
    patchViews(queryClient, tableId, (held) => views.removeView(held, viewId))
    // Everything keyed on the view goes with it -- its field options, its
    // aggregations -- rather than sitting in the cache for a view that no
    // longer exists.
    queryClient.removeQueries({ queryKey: qk.view(viewId) })
  })

  on('views_reordered', ({ queryClient }, data) => {
    patchViews(queryClient, data.table_id as number, (held) =>
      views.orderViews(held, data.order as number[]),
    )
  })

  /* --------------------------------------------------------------- fields */

  /**
   * Every field event reads the rows again, for every window on that table.
   *
   * All four change what a cell holds, not only what a column is called. A
   * created field is a key the held rows do not have; an updated one has had
   * its values rewritten by a type conversion; a deleted one takes the lookups
   * and formulas that reached through it with it, and those arrive as
   * `related_fields`; a restored one brings its values back.
   *
   * The schema cache is patched at once rather than after the rows arrive.
   * Upstream defers it, because it mounts an editable component per cell and
   * one handed a value of the wrong type can throw. Here a cell is a read-only
   * span over `unknown` and every reader already answers for a value that is
   * not the shape it expected -- so the cost is one frame of a column drawn as
   * its new type against its old values, and the price of avoiding it is tying
   * the schema cache to a fetch it does not otherwise know about.
   */
  const applyFields = (queryClient: QueryClient, fields: readonly Field[]): void => {
    for (const [tableId, held] of byTable(fields)) {
      queryClient.setQueryData<Field[]>(qk.fields(tableId), (current) =>
        current === undefined ? current : held.reduce(upsertField, current),
      )
    }
  }

  /**
   * A new column has no field options until somebody looks at it.
   *
   * The grid draws its columns from the fields rather than from the options, so
   * a column nobody has been told the width of appears at the default -- but the
   * options are cached forever, so without this it would stay there. Only the
   * views of that table, and only when the columns changed.
   */
  const staleFieldOptions = (queryClient: QueryClient, tableId: number): void => {
    for (const view of queryClient.getQueryData<View[]>(qk.views(tableId)) ?? []) {
      void queryClient.invalidateQueries({ queryKey: qk.fieldOptions(view.id) })
    }
  }

  for (const type of ['field_created', 'field_restored']) {
    on(type, ({ queryClient }, data) => {
      const field = data.field as Field
      applyFields(queryClient, [field, ...((data.related_fields ?? []) as Field[])])
      staleFieldOptions(queryClient, field.table_id)
      refreshRows(field.table_id)
    })
  }

  on('field_updated', ({ queryClient }, data) => {
    const field = data.field as Field
    applyFields(queryClient, [field, ...((data.related_fields ?? []) as Field[])])
    refreshRows(field.table_id)
  })

  on('field_deleted', ({ queryClient }, data) => {
    const tableId = data.table_id as number
    queryClient.setQueryData<Field[]>(qk.fields(tableId), (current) =>
      current === undefined ? current : removeField(current, data.field_id as number),
    )
    // The fields on the other side of a link, whose lookups and formulas have
    // just lost what they reached through.
    applyFields(queryClient, (data.related_fields ?? []) as Field[])
    staleFieldOptions(queryClient, tableId)
    refreshRows(tableId)
  })

  /* --------------------------------------------------- the lists on a view */

  /**
   * The fifteen events about a view's filters, sortings and group-bys.
   *
   * One shape seen four times: the payload names the child under a key called
   * after the event, and the deletion names it by id. Written as a loop rather
   * than as fifteen handlers, because fifteen copies of four lines is fifteen
   * places for one of them to be wrong.
   */
  const CHILDREN: { event: string; key: string; list: ChildList }[] = [
    { event: 'view_filter', key: 'view_filter', list: 'filters' },
    { event: 'view_filter_group', key: 'view_filter_group', list: 'filter_groups' },
    { event: 'view_sort', key: 'view_sort', list: 'sortings' },
    { event: 'view_group_by', key: 'view_group_by', list: 'group_bys' },
  ]

  for (const { event, key, list } of CHILDREN) {
    for (const suffix of ['created', 'updated']) {
      on(`${event}_${suffix}`, ({ queryClient }, data) => {
        const child = data[key] as { id: number; view: number }
        patchView(queryClient, child.view, (view) => upsertChild(view, list, child))
      })
    }

    on(`${event}_deleted`, ({ queryClient }, data) => {
      patchView(queryClient, data.view_id as number, (view) =>
        removeChild(view, list, data[`${key}_id`] as number),
      )
    })
  }

  for (const [event, key, list] of [
    ['view_sortings_prioritized', 'view_sort_ids', 'sortings'],
    ['view_group_bys_prioritized', 'view_group_by_ids', 'group_bys'],
  ] as [string, string, ChildList][]) {
    on(event, ({ queryClient }, data) => {
      patchView(queryClient, data.view_id as number, (view) =>
        prioritizeChildren(view, list, data[key] as number[]),
      )
    })
  }

  /**
   * How one view presents each of its fields.
   *
   * Merged rather than replaced. The event serialises without creating the
   * options a field has never had, so a column nobody has touched is simply
   * absent from it, while the endpoint's own answer has an entry for every
   * field -- writing the payload over the cache would drop those.
   */
  on('view_field_options_updated', ({ queryClient }, data) => {
    queryClient.setQueryData<ViewFieldOptions>(
      qk.fieldOptions(data.view_id as number),
      (current) =>
        current === undefined
          ? current
          : withFieldOptions(current, data.field_options as Record<number, object>),
    )
  })

  /* ---------------------------------------------------------- field rules */

  on('field_rule_added', ({ queryClient }, data) => {
    const rule = data.rule as FieldRule
    patchFieldRules(queryClient, rule.table_id, (rules) => addFieldRule(rules, rule))
  })

  on('field_rule_updated', ({ queryClient }, data) => {
    const rule = data.rule as FieldRule
    patchFieldRules(queryClient, rule.table_id, (rules) => updateFieldRule(rules, rule))
  })

  on('field_rule_deleted', ({ queryClient }, data) => {
    const rule = data.rule as FieldRule
    patchFieldRules(queryClient, rule.table_id, (rules) =>
      removeFieldRule(rules, rule.id),
    )
  })

  /* ------------------------------------------------- comments and history */

  for (const type of [
    'row_comment_added',
    'row_comment_changed',
    'row_comment_removed',
  ]) {
    on(type, ({ queryClient }, data) => {
      const comment = data.row_comment as { table_id: number; row_id: number }
      void queryClient.invalidateQueries({
        queryKey: qk.rowComments(comment.table_id, comment.row_id),
      })
    })
  }

  on('row_history_updated', ({ queryClient }, data) => {
    void queryClient.invalidateQueries({
      queryKey: qk.rowHistory(data.table_id as number, data.row_id as number),
    })
  })

  return () => {
    for (const remove of off) remove()
  }
}

/**
 * Applies an update to one table's cached views, if that table is on screen.
 *
 * The updaters signal "nothing changed" by returning the array they were given,
 * and only a changed one is written back -- so an event about a view this tab
 * is not holding costs a comparison and no render.
 */
function patchViews(
  queryClient: QueryClient,
  tableId: number,
  update: (views: View[]) => View[],
): void {
  queryClient.setQueryData<View[]>(qk.views(tableId), (held) =>
    held === undefined ? held : update(held),
  )
}

/**
 * Applies an update to one view, wherever it is cached.
 *
 * A view-child event names the view and nothing else -- no table, no database --
 * so there is nothing to key by. There is one cached list per table this tab
 * has opened, so this is a handful of arrays; the updaters answer with the view
 * they were given when nothing changed, and only a changed list is written
 * back, so an event about a filter a view never had costs a comparison and no
 * render.
 */
function patchView(
  queryClient: QueryClient,
  viewId: number,
  update: (view: View) => View,
): void {
  const entries = queryClient.getQueriesData<View[]>({
    predicate: (query) => query.queryKey[1] === 'table' && query.queryKey[3] === 'views',
  })

  for (const [key, held] of entries) {
    if (held === undefined) continue

    const view = held.find((one) => one.id === viewId)
    if (view === undefined) continue

    const next = update(view)
    // Not merely an optimisation. `setQueryData` marks the entry updated even
    // when structural sharing hands back the very array it already held, so
    // without this an event about a filter some view never had would touch
    // every cached view list in the tab.
    if (next === view) continue

    queryClient.setQueryData<View[]>(
      key,
      held.map((one) => (one === view ? next : one)),
    )
  }
}

function patchFieldRules(
  queryClient: QueryClient,
  tableId: number,
  update: (rules: FieldRule[]) => FieldRule[],
): void {
  queryClient.setQueryData<FieldRule[]>(qk.fieldRules(tableId), (current) =>
    current === undefined ? current : update(current),
  )
}
